import { CallbotTelephonyEntry, CallbotTelephonyPeriod } from '@/types/callbot'

// Правила оплаты телефонии — из макета и юзерстори.
export const TELEPHONY_PRESETS = [1000, 3000, 5000, 10000]
export const TELEPHONY_TOPUP_MIN_RUB = 100
export const TELEPHONY_TOPUP_MAX_RUB = 500_000
export const TELEPHONY_PAGE_SIZE = 10

export const TELEPHONY_PERIODS: Array<{ value: CallbotTelephonyPeriod; label: string }> = [
    { value: '30', label: 'Последние 30 дней' },
    { value: '7', label: 'Последние 7 дней' },
    { value: 'today', label: 'Сегодня' },
    { value: 'all', label: 'Всё время' },
]

// 5 654,94 ₽ — копейки показываем только когда они есть.
export function formatRub(value: number): string {
    const rounded = Math.round(value * 100) / 100
    const text = new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(rounded))
    return `${rounded < 0 ? '−' : ''}${text.replace(/ /g, ' ')} ₽`
}

export function formatSignedRub(value: number): string {
    return `${value > 0 ? '+ ' : '− '}${formatRub(Math.abs(value))}`
}

export function formatOperationDate(iso: string): string {
    const date = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}`
}

export function periodSince(period: CallbotTelephonyPeriod, now = new Date()): string | null {
    if (period === 'all') return null
    const since = new Date(now)
    if (period === 'today') since.setHours(0, 0, 0, 0)
    else since.setDate(since.getDate() - Number(period))
    return since.toISOString()
}

// «1 500,50», «1500.5», «1 500 ₽» → число; всё остальное — ошибка с текстом.
export function parseTopupAmount(raw: string): { amount: number | null; error: string } {
    const cleaned = raw.replace(/[\s ₽]/g, '').replace(',', '.')
    if (!cleaned) return { amount: null, error: '' }
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
        return { amount: null, error: 'Введите сумму цифрами, например 1 500 или 1 500,50' }
    }
    const amount = Number(cleaned)
    if (amount < TELEPHONY_TOPUP_MIN_RUB) {
        return { amount: null, error: `Минимальная сумма — ${formatRub(TELEPHONY_TOPUP_MIN_RUB)}` }
    }
    if (amount > TELEPHONY_TOPUP_MAX_RUB) {
        return { amount: null, error: `Максимальная сумма — ${formatRub(TELEPHONY_TOPUP_MAX_RUB)}` }
    }
    return { amount, error: '' }
}

export function operationTitle(entry: CallbotTelephonyEntry): string {
    if (entry.kind === 'topup') return 'Пополнение баланса'
    if (entry.kind === 'rental') return 'Аренда номера'
    if (entry.kind === 'call') return 'Звонок'
    return 'Корректировка'
}

export function operationCaption(entry: CallbotTelephonyEntry): string {
    if (entry.kind === 'topup') return 'ЮKassa'
    if (entry.kind === 'call') return 'Фактическое использование телефонии'
    return entry.comment
}

export type TelephonyWalletState = 'ok' | 'empty' | 'debt'

export function walletState(balance: number): TelephonyWalletState {
    if (balance < 0) return 'debt'
    if (balance === 0) return 'empty'
    return 'ok'
}

// Ошибки рассылки, связанные с деньгами и сутками аренды, — понятным текстом.
export const TELEPHONY_RUN_ERRORS: Record<string, string> = {
    INSUFFICIENT_BALANCE: 'Недостаточно средств на балансе. Пополните баланс и запустите рассылку снова.',
    BALANCE_EXHAUSTED: 'Баланс закончился, новые звонки остановлены. Пополните баланс и возобновите рассылку.',
    PAUSE_EXPIRED: 'Рассылка стояла на паузе до 23:00 и завершилась. Номера арендуются заново на следующий день.',
    NO_NUMBERS: 'Не удалось арендовать номера для звонков. Попробуйте запустить рассылку позже.',
}

export function runErrorText(code: string): string {
    return TELEPHONY_RUN_ERRORS[code] ?? `Ошибка рассылки: ${code}`
}

// «до 00:00 30.09» — граница оплаченной аренды.
export function formatPaidUntil(iso: string): string {
    const date = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `до ${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}.${pad(date.getMonth() + 1)}`
}
